<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import type { AgreementType } from '@/types'
import AgreementTypeComponent from '@/components/AgreementTypeComponent.vue';
import { onBeforeRouteLeave, useRouter } from 'vue-router';
import { useAgreementStore } from '@/stores/agreement';
import localStore from '@/stores/local'
import { storeToRefs } from 'pinia';
import BackComponent from '@/components/BackComponent.vue';
import BottomBarComponent from '@/components/BottomBarComponent.vue';
import NextComponent from '@/components/NextComponent.vue';
import { useVehicleStore } from '@/stores/vehicle';
import axios from 'axios';
import { useMessageStore } from '@/stores/messageStore';

const { agreement, agreementType } = storeToRefs(useAgreementStore())
const { isManualInput, brandName, manualVehicle, details } = storeToRefs(useVehicleStore())
const router = useRouter()
const message = useMessageStore()

const agreementTypes = ref<AgreementType[]>([])
const searching = ref(false)
const mileage = ref(agreement.value.oldMileage)
const age = computed(() => new Date().getFullYear() - (agreement.value.registrationYear as number))

function goBack() {
  if (isManualInput.value) {
    router.push({ name: 'manual-vehicle' })
  } else {
    router.push({ name: 'vehicle' })
  }
}

function isSelected(id: string) {
  return agreementType.value?.id === id
}

function onSelectType(type: AgreementType) {
  agreementType.value = type
  agreement.value.agreementId = type.agreementId
  localStore.setItem('agreementType', JSON.parse(JSON.stringify(agreementType.value)))
}

async function getAgreementTypes() {
  searching.value = true

  if (!agreement.value.registrationYear) {
    message.addError('Intet registreringsår blev fundet. Prøv at starte forfra')
    return
  }

  try {
    const { data } = await axios.get('app/agreement-types', {
      params: {
        mileage: mileage.value,
        brandName: brandName.value,
        registrationYear: agreement.value.registrationYear,
        // if it is manualVehicle, tack the id from it, if vin lookup, use details
        modelId: manualVehicle.value.model?.id != null 
        ? manualVehicle.value.model.id 
        : details.value?.modelId,
        isActive: true
      }
    })

    agreementTypes.value = data
  } catch (error) {
    console.error(error)
    message.addError('Ups! Noget gik galt. Hvis fejlen fortsætter kan du kontakte os på +45 3116 9202 eller sam@holco.dk.')
  } finally {
    searching.value = false
  }
}

onMounted(async () => {
  getAgreementTypes()
})
</script>

<template>
  <div id="type-container">
    <v-fade-transition group mode="in-out" hide-on-leave>
      <div v-if="searching">
        <v-skeleton-loader type="card" />
      </div>

      <template v-else-if="agreementTypes.length">
        <section class="type" v-for="type in agreementTypes" :key="type.agreementId">
          <AgreementTypeComponent
            :type="type"
            :active="isSelected(type.id)"
            @click="onSelectType(type)"
          />
        </section>
      </template>

      <v-empty-state
        v-else
        title="Ingen tilgængelige aftaletyper"
        text="Der blev desværre ikke fundet aftaletyper der matcher dit køretøj"
      />
    </v-fade-transition>
  </div>
  <BottomBarComponent>
    <BackComponent @click="goBack" />
    <NextComponent
      to="/agreement/details"
      :disabled="!agreementTypes.length"
    />
  </BottomBarComponent>
</template>

<style>
#type-container {
  display: flex;
  gap: 1rem;
  padding: 1rem;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scroll-snap-type: x mandatory;
}

#type-container .type {

  min-width: 320px;
  max-width: 450px;
  flex-grow: 1;
  scroll-snap-align: center;
  scroll-snap-stop: always;
}
</style>